import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Clock, User, Filter, RefreshCw } from "lucide-react";

// Libellés lisibles pour les actions techniques
const ACTION_LABELS = {
  "user.create": "Compte créé",
  "user.archive": "Compte archivé",
  "user.delete": "Compte supprimé",
  "user.restore": "Compte restauré",
  "shift.create": "Shift ajouté",
  "shift.delete": "Shift supprimé",
  "shift.publish": "Semaine publiée",
  "shift.cancel": "Shift annulé",
  "shift.uncancel": "Shift restauré",
  "period.create": "Période créée",
  "period.update": "Période modifiée",
  "dispo.remind_one": "Rappel dispo (1)",
  "dispo.remind_many": "Rappel dispos (masse)",
  "settings.update": "Réglages mis à jour",
};

const ACTION_COLORS = {
  "user.create": "bg-[#E8F0EE] text-[#1F423C]",
  "user.archive": "bg-[#FFF3E0] text-[#8B5A00]",
  "user.delete": "bg-[#FFEBEE] text-[#C62828]",
  "user.restore": "bg-[#E8F0EE] text-[#1F423C]",
  "shift.create": "bg-[#E8F0EE] text-[#2D5951]",
  "shift.delete": "bg-[#FFEBEE] text-[#C62828]",
  "shift.publish": "bg-[#E8F0EE] text-[#1F423C]",
  "shift.cancel": "bg-[#FFF3E0] text-[#8B5A00]",
  "shift.uncancel": "bg-[#E8F0EE] text-[#2D5951]",
  "period.create": "bg-[#F0F5F3] text-[#3D504B]",
  "period.update": "bg-[#F0F5F3] text-[#3D504B]",
  "dispo.remind_one": "bg-[#F0F5F3] text-[#3D504B]",
  "dispo.remind_many": "bg-[#F0F5F3] text-[#3D504B]",
  "settings.update": "bg-[#F0F5F3] text-[#3D504B]",
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}h${String(d.getMinutes()).padStart(2, "0")}`;
  } catch { return iso; }
}

export default function History() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [actorFilter, setActorFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = { limit: 300 };
      if (actorFilter !== "all") params.actor_id = actorFilter;
      if (actionFilter !== "all") params.action = actionFilter;
      const [logsRes, usersRes] = await Promise.all([
        api.get("/audit-logs", { params }),
        api.get("/users"),
      ]);
      setLogs(logsRes.data);
      setUsers(usersRes.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [actorFilter, actionFilter]);

  const actionCodes = useMemo(() => Array.from(new Set(logs.map((l) => l.action))).sort(), [logs]);

  return (
    <div className="space-y-6" data-testid="page-history">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-tiny">Journal d'activité</p>
          <h1 className="font-display text-3xl sm:text-4xl text-[#1A2B27] mt-1">Historique</h1>
          <p className="text-[#6b7f79] mt-1">Qui a fait quoi et quand — dernières 300 actions.</p>
        </div>
        <Button
          onClick={load}
          variant="outline"
          disabled={busy}
          data-testid="history-refresh"
          className="rounded-full border-[#DDE7E4] text-[#6b7f79]"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} /> Rafraîchir
        </Button>
      </div>

      <Card className="card-soft border-0">
        <CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-tiny flex items-center gap-1"><User className="w-3 h-3" /> Utilisateur</label>
              <Select value={actorFilter} onValueChange={setActorFilter}>
                <SelectTrigger data-testid="history-filter-actor" className="mt-1 h-10 rounded-xl border-[#DDE7E4]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les utilisateurs</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-tiny flex items-center gap-1"><Filter className="w-3 h-3" /> Action</label>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger data-testid="history-filter-action" className="mt-1 h-10 rounded-xl border-[#DDE7E4]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les actions</SelectItem>
                  {Object.entries(ACTION_LABELS).map(([code, label]) => (
                    <SelectItem key={code} value={code}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="card-soft border-0">
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <p className="p-6 text-center text-[#6b7f79]">Aucune action à afficher.</p>
          ) : (
            <div className="divide-y divide-[#DDE7E4]">
              {logs.map((l) => (
                <div
                  key={l.id}
                  data-testid={`history-row-${l.id}`}
                  className="p-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 hover:bg-[#F5FAF8]"
                >
                  <Clock className="w-3.5 h-3.5 text-[#6b7f79] shrink-0" />
                  <span className="text-xs text-[#6b7f79] font-mono">{fmtDate(l.at)}</span>
                  <span
                    className={`text-[10px] uppercase font-semibold rounded-full px-2 py-0.5 tracking-wide ${ACTION_COLORS[l.action] || "bg-[#F0F5F3] text-[#3D504B]"}`}
                  >
                    {ACTION_LABELS[l.action] || l.action}
                  </span>
                  <span className="text-sm text-[#1A2B27] font-semibold">{l.actor_name}</span>
                  {l.label && (
                    <span className="text-sm text-[#3D504B] flex-1 min-w-0 truncate" title={l.label}>
                      → {l.label}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
